/**
 * Redux slice for managing the current authorization context of the user.
 *
 * WARNING:
 *   The split between "authorization" and "currentAuthorization" is intentional,
 *   as it allows us to keep the list of available authorizations separate
 *   from the currently selected one, and to manage their loading and error states independently.
 */
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import * as uuid from "uuid";

import idb from "#/data-layer/idb";
import { USER_AUTHORIZATIONS } from "#/def/data-layer";
import ApiClient from "#/redux/api/ApiClient";
import { handleLoadingAndErrorState } from "#/redux/util/reduxUtils";
import { getErrorMessage } from "#/utils/APIUtils";
import * as localStorage from "#/utils/LocalStorageUtils";

const NAME = "activeAuthorization";
const baseUrl = "/api/varn_shema/zastopnistvo/user";

// Define importance order for sorting, from lowest to highest
//
// when indexOf returns -1 for unknown levels, they
// will be treated as lowest importance
const ORDERED = ["REJEC", "KONTROLOR", "SELEKCTIONIST", "VF", "BF"];

function sortByImportance(a, b) {
  const idxA = ORDERED.indexOf(a.nivoDostopa);
  const idxB = ORDERED.indexOf(b.nivoDostopa);
  return idxB - idxA;
}

/**
 * Validates an authorization ID against the available authorizations.
 *
 * @param {string} id
 * @param {Array<{ id: string }>} available
 * @returns
 */
function validateAuthorizationId(id) {
  // test the shape
  if (
    id === null ||
    id === undefined ||
    typeof id !== "string" ||
    id.trim().length !== uuid.NIL.length
  ) {
    return false;
  }

  // if (!uuid.validate(id)) {
  //   // currently, we get "abcdefab-abcd-feab-abab-abcdefabcdef"
  //   // which is not a valid UUID, but we want to be tolerant of that for now
  //   return false;
  // }

  return true;
}

// =============================================================================
// ASYNC THUNKS
// =============================================================================

export const fetchAvailableAuthorizations = createAsyncThunk(
  `${NAME}/fetchAvailable`,
  async (_, { rejectWithValue }) => {
    try {
      if (!navigator.onLine) {
        return await idb.all(USER_AUTHORIZATIONS);
      }

      const { data } = await ApiClient.get(baseUrl);

      // Replace cached entries so revoked authorizations don't linger offline.
      await idb.txReadwrite(USER_AUTHORIZATIONS, ({ store, resolve }) => {
        store.clear();
        (data ?? []).forEach((item) => store.put(item));
        resolve(data);
      });

      return data;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error));
    }
  }
);

const authorizationSlice = createSlice({
  name: NAME,
  initialState: {
    isLoading: false,
    error: null,

    // the currently selected authorization ID (user + nivoDostopa + subject)
    current: localStorage.get(localStorage.keys.AUTHORIZATION),

    // list of all authorizations available to the user,
    // used for populating dropdowns and validating current authorization
    available: [],
  },

  reducers: {
    /**
     * Sets the current authorization by ID.
     */
    setAuthorization(state, { payload }) {
      const id = payload || null;

      if (id && !validateAuthorizationId(id)) {
        throw new Error("Invalid authorization ID");
      }

      state.current = id;
    },
  },

  extraReducers: (builder) => {
    const thunks = [fetchAvailableAuthorizations];

    builder.addCase(fetchAvailableAuthorizations.fulfilled, (state, action) => {
      const data = action.payload || [];

      data.sort(sortByImportance);

      // verify that current authorization is still valid
      // and exists in the newly fetched available authorizations
      if (!validateAuthorizationId(state.current)) {
        state.current = null;
      }

      // When the list of available authorizations is updated,
      // we need to check if the current authorization ID is still valid.
      // If the current ID does not exist in the new list of available authorizations,
      // we should clear it to prevent the application from being in an invalid state.
      if (!data.some((auth) => auth.id === state.current)) {
        state.current = null;
      }

      state.available = data;
    });

    handleLoadingAndErrorState(builder, thunks);
  },
});

/**
 * Authorization setter and clearer actions.
 */
export const { setAuthorization, clearAuthorization } =
  authorizationSlice.actions;

export function currentAuthorization(state) {
  return state.current;
}

/**
 * Authorization slice reducer.
 */
export default authorizationSlice.reducer;
